import { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { DraftConfiguration } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { getPrintAreaLabel } from '@/model/printArea/getPrintAreaLabel';
import { getIsSingleConfigurationPrintType } from '@/model/printType/getIsSingleConfigurationPrintType';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getProductType } from '@/redux/draft/getters/getProductType';
import { getSortedConfigurationsOnCurrentView } from '@/redux/draft/getters/getSortedConfigurationsOnCurrentView';
import { duplicateConfigurationAction } from '@/components/draft/draftState/actions/duplicateConfigurationAction';
import { removeConfigurationAction } from '@/components/draft/draftState/actions/removeConfigurationAction';
import { swapConfigurationAction } from '@/components/draft/draftState/actions/swapConfigurationAction';
import { MenuButton } from '../common/Menu';
import { Delete } from '../common/icons/Delete';
import { Duplicate } from '../common/icons/Duplicate';
import { LayerDown } from '../common/icons/LayerDown';
import { LayerUp } from '../common/icons/LayerUp';
import { MoveTo } from '../common/icons/MoveTo';
import './ConfigurationControls.scss';

interface Props {
  configuration: DraftConfiguration;
  onPaSelectorClick?: () => void;
}

export const ConfigurationControls: FunctionComponent<Props> = ({
  configuration,
  onPaSelectorClick,
}) => {
  const dispatch = useReduxDraftDispatch();
  const configurationsOnView = useSelector(getSortedConfigurationsOnCurrentView);
  const indexOnView = configurationsOnView.indexOf(configuration);
  const productType = useSelector(getProductType);

  function handleDuplication() {
    trackFeature('duplicate-configuration');
    dispatch(duplicateConfigurationAction(configuration));
  }

  function handleDelete() {
    trackFeature('delete-configuration');
    dispatch(removeConfigurationAction(configuration));
  }

  function handleLayerUp() {
    trackFeature('configuration-layer-up');
    const next = configurationsOnView[indexOnView + 1];
    dispatch(swapConfigurationAction(configuration, next));
  }

  function handleLayerDown() {
    trackFeature('configuration-layer-down');
    const next = configurationsOnView[indexOnView - 1];
    dispatch(swapConfigurationAction(configuration, next));
  }

  return (
    <div className="configuration-controls">
      <MenuButton
        icon={LayerUp}
        onClick={handleLayerUp}
        disabled={!configurationsOnView.length || indexOnView === configurationsOnView.length - 1}
        text={t('configuration.basic.layerUp')}
      />
      <MenuButton
        icon={LayerDown}
        onClick={handleLayerDown}
        disabled={!configurationsOnView.length || indexOnView === 0}
        text={t('configuration.basic.layerDown')}
      />
      {onPaSelectorClick && productType && productType.views.length > 1 && (
        <MenuButton icon={MoveTo} onClick={onPaSelectorClick} text={t(getPrintAreaLabel())} />
      )}
      <MenuButton
        icon={Duplicate}
        onClick={handleDuplication}
        text={t('configuration.basic.duplicate')}
        disabled={getIsSingleConfigurationPrintType(configuration.printType)}
      />
      <MenuButton icon={Delete} onClick={handleDelete} text={t('configuration.basic.delete')} />
    </div>
  );
};
